import InfiniteScroll from 'react-infinite-scroll-component';
import { transformUserRoleToIssuerRole } from 'auth';
import { useCallback, useContext, useState } from 'react';
import IssuerModal from '@/modules/common/components/modals/IssuerModal.tsx';
import { useUpdateUserPermissionsMutation } from '@/modules/common/queries/permissions.ts';
import { AdminRegisterModal } from '@/modules/admin-dashboard/components/modals/AdminRegisterModal.tsx';
import {
  useIssuerAddNewIssuerMutation,
  useIssuerDeleteIssuerMutation,
} from '@/modules/issuer-dashboard/queries/issuers.ts';
import { useGetIssuersByRegisterQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import {
  Avatar,
  Button,
  ConfirmModal,
  DisclosureListItem,
  ModalProviderContext,
  Spinner,
} from 'ui';
import { EditIcon, EmptyFolderIcon, TrashGrayIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import { Issuer, RegisterResponse } from 'api';

interface IssuerRegisterIssuersItemProps {
  register: RegisterResponse;
}

const IssuerRegisterIssuersItem = ({ register }: IssuerRegisterIssuersItemProps) => {
  const { openModal } = useContext(ModalProviderContext);
  const [isIssuersBeenOpened, setIsIssuersBeenOpened] = useState(false);

  const { data, isFetched, fetchNextPage, hasNextPage } = useGetIssuersByRegisterQuery(
    { id: register.id, page: 1, page_size: 15 },
    !isIssuersBeenOpened,
  );
  const { mutateAsync: deleteIssuerFromRegister } = useIssuerDeleteIssuerMutation(register.id);

  const handleOpenAddNewIssuerModal = useCallback(() => {
    openModal(IssuerModal<{ registerId: number }>, {
      register,
      mutation: [useIssuerAddNewIssuerMutation, { registerId: register.id }],
    });
  }, [openModal, register]);

  const handleOpenEditRegisterModal = useCallback(() => {
    openModal(AdminRegisterModal, { redactionRegisterId: register.id, role: 'issuer' });
  }, [register.id, openModal]);

  const issuers = (data?.pages || []).flatMap(({ data }) => data);

  const handleOpenRedactIssuerModal = useCallback(
    (issuer: Issuer) => {
      const userRole = (issuer?.user_registers || []).find(r => r.id === register.id)?.user_role;
      const issuerRole = userRole ? transformUserRoleToIssuerRole(userRole) : null;
      openModal(IssuerModal, {
        issuer: issuerRole
          ? { ...issuer, issuer_role: issuerRole, user_registers: issuer.user_registers || [] }
          : { ...issuer, user_registers: issuer.user_registers || [] },
        register,
        isUpdating: true,
        mutation: [useUpdateUserPermissionsMutation, {}],
      });
    },
    [openModal, register],
  );

  const handleOpenDeleteIssuerFromRegisterModal = useCallback(
    (issuer: Issuer, issuerRegisterRelationId: number) => {
      openModal(ConfirmModal, {
        title: (
          <span>
            Delete{' '}
            <span className="text-blue-600">
              {issuer.first_name} {issuer.last_name}
            </span>{' '}
            from <span className="text-blue-600">{register.name}</span>
          </span>
        ),
        onConfirm: async () => {
          deleteIssuerFromRegister(issuerRegisterRelationId);
        },
        closeAfterConfirm: true,
      });
    },
    [deleteIssuerFromRegister, openModal, register.name],
  );

  const customBottomContent = (
    <>
      {!isFetched && (
        <div className="flex items-center justify-center p-4">
          <Spinner />
        </div>
      )}
      {isFetched && !!issuers.length && (
        <div id={`register-issuers-${register.id}`} className="overflow-y-auto max-h-[400px]">
          <InfiniteScroll
            next={fetchNextPage}
            dataLength={issuers.length}
            hasMore={!!hasNextPage}
            loader={
              <div className="flex justify-center items-center h-16 w-full">
                <Spinner size="md" variant="primary" />
              </div>
            }
            className="w-full"
            scrollableTarget={`register-issuers-${register.id}`}
          >
            {issuers.map(issuer => {
              const { id, first_name, last_name, avatar_url, user_registers } = issuer;
              const userRegister = user_registers.find(
                ({ register_id }) => register_id === register.id,
              );
              if (!userRegister) return null;
              return (
                <div
                  key={id}
                  className="w-full flex items-center justify-between px-2 sm:px-6 py-[14px] border-t border-gray-200"
                >
                  <div className="flex items-center gap-3">
                    <Avatar
                      firstName={first_name}
                      lastName={last_name}
                      src={avatar_url}
                      withBorder={true}
                    />
                    <p className="text-[14px] leading-[22px] font-medium">
                      {first_name} {last_name}
                    </p>
                  </div>
                  <div className="space-x-3 select-none">
                    <button onClick={handleOpenRedactIssuerModal.bind(null, issuer)}>
                      <img src={EditIcon} alt="Edit register permissons" />
                    </button>
                    <button
                      onClick={handleOpenDeleteIssuerFromRegisterModal.bind(
                        null,
                        issuer,
                        userRegister.id,
                      )}
                    >
                      <img src={TrashGrayIcon} alt="Remove register" />
                    </button>
                  </div>
                </div>
              );
            })}
          </InfiniteScroll>
        </div>
      )}
      {isFetched && !issuers.length && (
        <div className="flex flex-col justify-center items-center gap-6 h-[200px] border-t">
          <div className="flex items-center justify-center w-[36px] h-[36px] bg-[#E3E3E3] rounded-full">
            <img alt="no results" src={EmptyFolderIcon} className="w-4 h-4" />
          </div>
          <div className="text-xl text-neutral-900">
            There are no more issuers except you in this register
          </div>
        </div>
      )}
    </>
  );

  return (
    <DisclosureListItem
      leftContent={
        <Avatar
          firstName={register.name}
          lastName={String(register.id)}
          src={register.icon_url}
          className="mr-2"
          withBorder={true}
          withRounded={false}
        />
      }
      leftMainString={register.name}
      leftSubduedContentSet={[`Type: ${fixNameEnum(register.type)}`]}
      classes={{
        content: '!grid grid-cols-[minmax(0,1fr)_auto] gap-3',
        leftContent: 'min-w-0 flex-1 [&>div]:min-w-0',
        leftMainString: 'break-words',
        rightContentUp: '!flex',
        rightContent: '!flex !pl-0 w-8 col-auto',
      }}
      onOpen={setIsIssuersBeenOpened.bind(null, true)}
      bottomCustomContent={customBottomContent}
      leftSubduedContent={
        <span
          data-mobile-actions
          className="flex flex-wrap gap-2 items-center mt-2"
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <Button variant="plain" className="w-fit" onClick={handleOpenAddNewIssuerModal}>
            Add issuer
          </Button>
          <Button variant="plain" className="w-fit" onClick={handleOpenEditRegisterModal}>
            Edit Register
          </Button>
        </span>
      }
    />
  );
};

export default IssuerRegisterIssuersItem;
